import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserSearch, type UserSearchResult } from './useUserSearch'
import { useStartImpersonation } from './useStartImpersonation'

export interface ImpersonationSearchModalProps {
  isOpen: boolean
  onClose: () => void
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return v
}

export function ImpersonationSearchModal({ isOpen, onClose }: ImpersonationSearchModalProps) {
  const { t } = useTranslation('administration')
  const dialogRef = useRef<HTMLDialogElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [raw, setRaw] = useState('')
  const debounced = useDebounced(raw, 250)
  const { data, isLoading, error } = useUserSearch(debounced)
  const start = useStartImpersonation()

  // Mirror the showModal/close pattern from AddTeamsToGroupDialog and GroupFormDialog.
  useEffect(() => {
    const d = dialogRef.current
    if (!d) return
    if (isOpen && !d.open) d.showModal()
    if (!isOpen && d.open) d.close()
  }, [isOpen])

  // Focus the search input after the dialog opens.
  useEffect(() => {
    if (isOpen) {
      setRaw('')
      const id = requestAnimationFrame(() => searchRef.current?.focus())
      return () => cancelAnimationFrame(id)
    }
  }, [isOpen])

  return (
    <dialog
      ref={dialogRef}
      className="admin-dialog admin-dialog--wide"
      aria-modal="true"
      aria-labelledby="impersonation-modal-title"
      onClose={onClose}
      onCancel={onClose}
    >
      <div className="admin-dialog__inner">
        <h2 id="impersonation-modal-title" className="admin-dialog__title">
          {t('impersonation.modal.title')}
        </h2>

        <label className="admin-form__field">
          <span className="sr-only">{t('impersonation.modal.searchPlaceholder')}</span>
          <input
            ref={searchRef}
            type="search"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={t('impersonation.modal.searchPlaceholder')}
          />
        </label>

        {error != null && (
          <p role="alert" className="admin-form__error">
            {(error as Error).message}
          </p>
        )}

        {isLoading && <p>{t('loading')}</p>}

        {!isLoading && data != null && data.length === 0 && debounced.trim().length >= 2 && (
          <p className="text-muted">{t('impersonation.modal.noResults')}</p>
        )}

        <ul className="admin-dialog__team-picker">
          {data?.map((u) => (
            <UserRow key={u.id} user={u} onPick={(uid) => start.mutate(uid)} />
          ))}
        </ul>

        <div className="admin-dialog__actions">
          <button type="button" onClick={onClose}>
            {t('actions.cancel')}
          </button>
        </div>
      </div>
    </dialog>
  )
}

interface UserRowProps {
  user: UserSearchResult
  onPick: (uid: string) => void
}

function UserRow({ user, onPick }: UserRowProps) {
  const { t } = useTranslation('administration')

  return (
    <li className="admin-dialog__team-picker-row">
      <div>
        <strong>{user.displayName}</strong>
        <span>{user.email}</span>
      </div>
      <button type="button" onClick={() => onPick(user.id)}>
        {t('impersonation.modal.action')}
      </button>
    </li>
  )
}
